import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, TrendingUp, Shield, Zap } from 'lucide-react';
import { ParticlesCanvas } from '../ui/ParticlesCanvas';
function FloatingCard({
  className,
  children
}: {
  className: string;
  children: React.ReactNode;
}) {
  return <div className={`absolute bg-white dark:bg-dark-card rounded-2xl shadow-xl border border-gray-100 dark:border-dark-border p-3 ${className}`} data-id="element-827">
      {children}
    </div>;
}
export function HeroSection() {
  const navigate = useNavigate();
  return <section className="relative overflow-hidden" data-id="element-828">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]" data-id="element-829">
        {/* Left: Blue background */}
        <div className="bg-primary-500 relative flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-20" data-id="element-830">
          {/* Particles */}
          <ParticlesCanvas colors={['#ffffff', '#a5d8ff', '#e7f5ff', '#74c0fc']} count={48} speed={0.28} connectionDistance={100} data-id="element-831" />
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10" data-id="element-832">
            <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-white" data-id="element-833" />
            <div className="absolute bottom-20 right-5 w-60 h-60 rounded-full bg-white" data-id="element-834" />
          </div>

          <div className="relative z-10 max-w-lg" data-id="element-835">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-white/30" data-id="element-836">
              <Zap size={12} className="text-accent-400" data-id="element-837" />
              AI-Powered Financial Intelligence
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6" data-id="element-838">
              Your money,
              <br data-id="element-839" />
              <span className="text-accent-400" data-id="element-840">finally</span>
              <br data-id="element-841" />
              making sense.
            </h1>

            <p className="text-lg text-primary-100 mb-8 leading-relaxed" data-id="element-842">
              AI-powered financial management that tracks spending, analyzes
              budgets, and helps you reach your goals faster.
            </p>

            <div className="flex flex-wrap gap-3 mb-8" data-id="element-843">
              <button onClick={() => navigate('/signup')} className="flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-black font-bold px-6 py-3 rounded-xl transition-all hover:scale-105 active:scale-95 shadow-lg" data-id="element-844">
                Get started free <ArrowRight size={16} data-id="element-845" />
              </button>
              <button onClick={() => navigate('/login')} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-xl border border-white/30 transition-colors backdrop-blur-sm" data-id="element-846">
                See how it works
              </button>
            </div>

            {/* Social proof */}
            <div className="flex items-center gap-3" data-id="element-847">
              <div className="flex -space-x-2" data-id="element-848">
                {['#1566FF', '#39E75F', '#F59E0B', '#EC4899', '#8B5CF6'].map((color, i) => <div key={i} className="w-8 h-8 rounded-full border-2 border-primary-500 flex items-center justify-center text-white text-xs font-bold" style={{
                backgroundColor: color
              }} data-id="element-849">
                      {String.fromCharCode(65 + i)}
                    </div>)}
              </div>
              <div data-id="element-850">
                <div className="flex items-center gap-1" data-id="element-851">
                  {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-accent-400 fill-accent-400" data-id="element-852" />)}
                </div>
                <p className="text-xs text-primary-200" data-id="element-853">
                  Trusted by 100,000+ users
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Dashboard mockup */}
        <div className="bg-gray-900 dark:bg-dark-bg relative flex items-center justify-center p-8 overflow-hidden min-h-[400px]" data-id="element-854">
          {/* Particles */}
          <ParticlesCanvas colors={['#228be6', '#74c0fc', '#4dabf7', '#1c7ed6', '#a5d8ff']} count={38} speed={0.22} connectionDistance={95} data-id="element-855" />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/50 to-gray-900" data-id="element-856" />

          {/* Mock dashboard */}
          <div className="relative z-10 w-full max-w-sm" data-id="element-857">
            {/* Main card */}
            <div className="bg-dark-card rounded-2xl p-5 border border-dark-border shadow-2xl" data-id="element-858">
              <div className="flex items-center justify-between mb-4" data-id="element-859">
                <div data-id="element-860">
                  <p className="text-xs text-gray-400" data-id="element-861">Net Worth</p>
                  <p className="text-2xl font-black text-white" data-id="element-862">$76,420</p>
                </div>
                <div className="flex items-center gap-1 bg-accent-500/20 text-accent-400 text-xs font-semibold px-2 py-1 rounded-full" data-id="element-863">
                  <TrendingUp size={11} data-id="element-864" /> +11.1%
                </div>
              </div>
              {/* Mini chart bars */}
              <div className="flex items-end gap-1 h-16 mb-3" data-id="element-865">
                {[40, 55, 45, 65, 58, 72, 80, 75, 85, 78, 90, 100].map((h, i) => <div key={i} className="flex-1 rounded-sm transition-all" style={{
                height: `${h}%`,
                backgroundColor: i === 11 ? '#39E75F' : '#1566FF',
                opacity: 0.6 + i / 11 * 0.4
              }} data-id="element-866" />)}
              </div>
              <div className="grid grid-cols-2 gap-2" data-id="element-867">
                {[{
                label: 'Income',
                value: '$7,489',
                color: 'text-accent-400'
              }, {
                label: 'Spending',
                value: '$5,317',
                color: 'text-red-400'
              }].map(s => <div key={s.label} className="bg-dark-hover rounded-xl p-2.5" data-id="element-868">
                    <p className="text-xs text-gray-400" data-id="element-869">{s.label}</p>
                    <p className={`text-sm font-bold ${s.color}`} data-id="element-870">{s.value}</p>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Floating cards */}
          <FloatingCard className="top-8 right-4 animate-float" data-id="element-871">
            <div className="flex items-center gap-2" data-id="element-872">
              <div className="w-7 h-7 rounded-lg bg-accent-500/20 flex items-center justify-center" data-id="element-873">
                <TrendingUp size={13} className="text-accent-500" data-id="element-874" />
              </div>
              <div data-id="element-875">
                <p className="text-xs font-semibold text-gray-800 dark:text-gray-200" data-id="element-876">
                  Savings up
                </p>
                <p className="text-xs text-accent-600 font-bold" data-id="element-877">
                  +$450 this month
                </p>
              </div>
            </div>
          </FloatingCard>

          <FloatingCard className="bottom-12 left-4" style={{
          animationDelay: '2s'
        } as any} data-id="element-878">
            <div className="flex items-center gap-2" data-id="element-879">
              <div className="w-7 h-7 rounded-lg bg-primary-500/20 flex items-center justify-center" data-id="element-880">
                <Shield size={13} className="text-primary-500" data-id="element-881" />
              </div>
              <div data-id="element-882">
                <p className="text-xs font-semibold text-gray-800 dark:text-gray-200" data-id="element-883">
                  Credit Score
                </p>
                <p className="text-xs text-primary-600 font-bold" data-id="element-884">
                  742 · Very Good
                </p>
              </div>
            </div>
          </FloatingCard>
        </div>
      </div>
    </section>;
}